import React from 'react';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import {
  GridRowSelectiomModel
} from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';

// Styles
import { useTheme } from '@mui/material/styles';

// Components
import EmailForm from './EmailForm';
import NightsGrid from './NightsGrid'
import NightsMobile from './NightsMobile'
import NightsToolbar from './NightsToolbar'
import RequestSummaryDialog from './RequestSummaryDialog'

import {
  HTTPHeaders,
} from "../../interfaces";

import {
  DateRange,
  RequestGrid,
  RequestGridHeader,
  RequestGridRow,
  RequestStatusChange,
} from '../../models';

import {
  API_URL,
  NULL_ID,
  RequestStatusUpdate,
} from '../../global';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';
// Responsive hooks
import { useResponsive } from '../../hooks/UseResponsive';

interface NightsProps {
  dateRange: DateRange;
  nights: number;
}

const Nights: React.FC<NightsProps> = (props) => {

  const { enqueueSnackbar } = useSnackbar();
  const { getAccessToken, isSignupAdmin } = useAuth0User();
  const { isMobile } = useResponsive();

  const [emailDrawerVisible, setEmailDrawerVisible] = React.useState<boolean>(false);
  const [guestsSelected, setGuestsSelected] = React.useState<Array<number>>([]);
  const [headers, setHeaders] = React.useState<Array<RequestGridHeader>>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [membersSelected, setMembersSelected] = React.useState<Array<number>>([]);
  const [requestsSelected, setRequestsSelected] = React.useState({ type: 'include', ids: new Set() });
  const [rows, setRows] = React.useState<Array<RequestGridRow>>([]);
  const [signupID, setSignupID] = React.useState<number>(NULL_ID);
  const [summaryDialogOpen, setSummaryDialogOpen] = React.useState<boolean>(false);

  // #region Styles

  const theme = useTheme();
  const styles = {
    emailDrawer: {
      flexGrow: 1,
      padding: theme.spacing(2)
    },
    gridContainer: {
      flexGrow: 1,
      height: 440,
      padding: theme.spacing(0, 1, 0, 1),
    },
    toolbarContaner: {
      margin: theme.spacing(1, 1, 1, 1),
    }
  };

  // #endregion

  // #region Methods

  /*
 * Fetch requests based upon filter
 */
  const fetchGridData = async () => {

    setIsLoading(true);

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    // Always show current date forward
    const params = new URLSearchParams();
    if (props.dateRange !== undefined) {
      if (props.dateRange.startDate) {
        params.append('startDate', props.dateRange.startDate);
      }
      if (props.dateRange.endDate) {
        params.append('endDate', props.dateRange.endDate);
      }
    }

    const url = new URL(`${API_URL}/v1/requests/requestgrid`);
    url.search = params.toString();

    await fetch(url.toString(), {
      method: 'GET',
      cache: 'no-cache',
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          setIsLoading(false);
          throw new Error('Requests not retrieved');
        }
        return response.json()
      })
      .then((gridData: RequestGrid) => {
        setHeaders(gridData.headers);
        setRows(gridData.rows);
        //setRequestsSelected([]);
        setIsLoading(false);
      })
      .catch(err => {
        setIsLoading(false);
      });
  }

  /*
  * Action to send email to selected members
  */
  const showEmailForm = () => {
    setEmailDrawerVisible(true);
  }

  /*
   * Update request status
   */
  const updateStatus = async (action: RequestStatusUpdate) => {

    const statusChange = new RequestStatusChange();
    statusChange.guestSignupIDs = guestsSelected;
    statusChange.memberSignupIDs = membersSelected;
    statusChange.statusID = action;

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const url = new URL(`${API_URL}/v1/requests/status/update`);

    await fetch(url.toString(), {
      body: JSON.stringify(statusChange),
      headers: headers,
      method: 'POST'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Lodge use action status error');
        }
        fetchGridData();
        enqueueSnackbar('Lodge use request status updated for selected individuals', { variant: 'success' });
      })
      .catch(err => {
        throw new Error('Lodge use action status error');
      });

  }

  // #endregion

  // #region Event handlers

  /*
  * Handle hiding the email drawer
  */
  const handleHideEmailForm = () => {
    setEmailDrawerVisible(false);
  };

  /*
  * Handle a request summary dialog close
  */
  const handleSummaryDialogClose = () => {
    setSummaryDialogOpen(false);
  }

  /*
  * Handle request summary dialog open
  */
  const handleSummaryDialogOpen = (id: number) => {
    setSignupID(id);
    setSummaryDialogOpen(true);
  }

  /*
  * Handle when a reqest is selected
  */
  const handleRequestsSelected = (selectedRows: GridRowSelectiomModel) => {

    const ids: number[] = [...selectedRows.ids];

    // Extract member and guest request IDs for status update
    const g = rows.filter(r => ids.includes(r.rowID)).flatMap(r => r.isGuest ? r.requestID : []);
    setGuestsSelected(g);

    const m = rows.filter(r => ids.includes(r.rowID)).flatMap(r => !r.isGuest ? r.requestID : []);
    setMembersSelected(m);

    // Maps back to grid checkboxes
    setRequestsSelected(selectedRows);

  }

  React.useEffect(() => {
    if (props.dateRange.startDate && props.dateRange.endDate) {
      fetchGridData();
    } else {
      setHeaders([]);
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dateRange.startDate, props.dateRange.endDate]);

  // #endregion

  return (
    <>
      {!isMobile &&
        <>
          {isSignupAdmin() &&
            <Box sx={styles.toolbarContaner}>
              <NightsToolbar
                handleShowEmailForm={showEmailForm}
                handleStatusChange={updateStatus}
                membersSelected={membersSelected.length}
                selectedCount={requestsSelected.length}
              />
            </Box>
          }
          <Box sx={styles.gridContainer}>
            <NightsGrid
              dateRange={props.dateRange}
              handleRequestsSelected={handleRequestsSelected}
              handleRequestSummary={handleSummaryDialogOpen}
              headers={headers}
              isLoading={isLoading}
              nights={props.nights}
              requestsSelected={requestsSelected}
              rows={rows}
            />
          </Box>
        </>
      }
      {isMobile &&
        <NightsMobile
          dateRange={props.dateRange}
          headers={headers}
          isLoading={isLoading}
          nights={props.nights}
          rows={rows}
        />
      }
      <RequestSummaryDialog
        handleClose={handleSummaryDialogClose}
        id={signupID}
        show={summaryDialogOpen}
      />
      <Drawer
        variant="temporary"
        anchor="right"
        open={emailDrawerVisible}
        onClose={handleHideEmailForm}
      >
        <Box sx={styles.emailDrawer}>
          <EmailForm
            ids={membersSelected}
          />
        </Box>
      </Drawer>
    </>
  );

}

export default Nights;